// What a detail page's "Back" control should do with one click.
//
// The control is an ANCHOR pointing at a fallback page (see
// `components/back-link.tsx` for why), so most clicks must be left alone: a
// ctrl/cmd/middle click is the browser opening that href in a new tab, and
// stealing it to run a history hop would break the one gesture an operator uses
// to keep the list open while reading a record. Only the plain left click, on a
// page that has somewhere to go back to, is ours to intercept.
//
// Both answers live here rather than in the component so they can be tested
// without a DOM: these tests run in the node environment.

/** The parts of a click this decision reads, so a test can pass a plain object. */
export interface BackClick {
  /** 0 is the primary (left) button. */
  readonly button: number;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly defaultPrevented: boolean;
}

/**
 * `"history"` — intercept this click and walk the router's history back one
 * entry, which is the page the operator actually came from.
 *
 * `"href"` — leave the click alone, and let the anchor's own href take it to the
 * declared fallback (or to a new tab, when the browser is going to do that).
 */
export type BackTarget = "history" | "href";

export function backTarget(click: BackClick, canGoBack: boolean): BackTarget {
  // Nothing behind us in this tab: a deep link from Slack, a pasted URL, a fresh
  // tab. The fallback page is the whole point of declaring one.
  if (!canGoBack) return "href";
  // Someone ahead of us already handled it.
  if (click.defaultPrevented) return "href";
  // Middle click, or a modifier: the browser is opening a new tab or window.
  if (click.button !== 0) return "href";
  if (click.metaKey || click.ctrlKey || click.shiftKey || click.altKey) return "href";
  return "history";
}

/**
 * What the control should SAY. A back hop lands wherever the operator came from,
 * which is not necessarily the fallback, so the specific label ("Back to
 * Explorer") is only honest when the fallback is in fact where the click goes.
 */
export function backLabel(fallbackLabel: string, canGoBack: boolean): string {
  return canGoBack ? "Back" : fallbackLabel;
}
